import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  ManualPaymentModel,
  PaymentRequestDecisionFormInput,
  PaymentRequestFormInput,
  PaymentRequestHistoryModel,
  PaymentRequestModel,
  ManualPaymentFormInput,
} from "../../viewModels/PaymentRequest/PaymentRequestModel";
import { PaymentRequestPaymentStatus, PaymentRequestStatus } from "../../dto/PaymentRequest/PaymentRequestDto";
import { PaymentsFiltersType } from "../../components/V2Components/PaymentFilters";
import PaymentModel from "../../viewModels/Payments/PaymentModel";
import SortObject from "../../types/SortObject";

export type PaymentRequestsFiltersType = {
  id?: number;
  requestNumber?: string;
  contractorIds?: Array<number>;
  statuses?: Array<PaymentRequestStatus>;
  paymentStatuses?: Array<PaymentRequestPaymentStatus>;
  creatorUserId?: number;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  currencyId?: number;
  paymentKindId?: number;
  paymentTypeId?: number;
  plannedPayment?: boolean;
  amountFrom?: number;
  amountTo?: number;
  overdue?: boolean;
};

const pageSizeLsKey = "payment_requests_pageSize";
const pageSizeFromLs = localStorage.getItem(pageSizeLsKey);

const initialState: {
  loading: boolean;
  loadingData: boolean;
  actionLoading: boolean;
  page: number;
  pageSize: number;
  total: number;
  sort?: SortObject;
  filters: PaymentRequestsFiltersType;
  requests: Array<PaymentRequestModel>;
  summary?: {
    draftCount: number;
    pendingApprovalCount: number;
    approvedCount: number;
    dealRequestCount: number;
    returnedForRevisionCount: number;
    rejectedCount: number;
    unpaidCount: number;
    plannedCount: number;
    partiallyPaidCount: number;
    paidCount: number;
    overdueCount: number;
    plannedPaymentCount: number;
  };
  selectedRequest?: PaymentRequestModel;
  historyModalOpen: boolean;
  historyLoading: boolean;
  history?: PaymentRequestHistoryModel;
  exportLoading: boolean;
  selectedManualPayment?: ManualPaymentModel;
  requestDrawerOpen: boolean;
  requestDrawerMode: "create" | "edit";
  detailDrawerOpen: boolean;
  decisionModalOpen: boolean;
  decisionAction?: "approve" | "reject" | "return";
  bulkDecisionModalOpen: boolean;
  bulkDecisionAction?: "approve" | "reject" | "return";
  bulkDecisionIds: Array<number>;
  manualPaymentModalOpen: boolean;
  manualPaymentModalMode: "create" | "edit";
  paymentsLoading: boolean;
  paymentsLoadingData: boolean;
  paymentsPage: number;
  paymentsPageSize: number;
  payments?: Array<PaymentModel>;
  paymentsTotal?: number;
  paymentsSort?: SortObject;
  paymentsFilters?: PaymentsFiltersType;
  selectedPaymentId?: number;
  selectedPaymentIds: Array<number>;
} = {
  loading: true,
  loadingData: false,
  actionLoading: false,
  page: 1,
  pageSize: pageSizeFromLs ? +pageSizeFromLs : 10,
  total: 0,
  sort: undefined,
  filters: {},
  requests: [],
  historyModalOpen: false,
  historyLoading: false,
  exportLoading: false,
  requestDrawerOpen: false,
  requestDrawerMode: "create",
  detailDrawerOpen: false,
  decisionModalOpen: false,
  bulkDecisionModalOpen: false,
  bulkDecisionIds: [],
  manualPaymentModalOpen: false,
  manualPaymentModalMode: "create",
  paymentsLoading: true,
  paymentsLoadingData: true,
  paymentsPage: 1,
  paymentsPageSize: 10,
  selectedPaymentIds: [],
};

const paymentRequestsSlice = createSlice({
  name: "paymentRequests",
  initialState,
  reducers: {
    loadPage(state) {
      state.loadingData = true;
    },
    loadPageSuccess(
      state,
      action: PayloadAction<{
        requests: Array<PaymentRequestModel>;
        total: number;
        summary: NonNullable<typeof initialState.summary>;
      }>,
    ) {
      state.loading = false;
      state.loadingData = false;
      state.requests = action.payload.requests;
      state.total = action.payload.total;
      state.summary = action.payload.summary;
      if (state.selectedRequest) {
        const selectedRequest = action.payload.requests.find((request) => request.id === state.selectedRequest?.id);
        state.selectedRequest = selectedRequest || state.selectedRequest;
      }
    },
    loadPageReject(state) {
      state.loading = false;
      state.loadingData = false;
    },
    exportTable(state) {
      state.exportLoading = true;
    },
    exportTableSuccess(state) {
      state.exportLoading = false;
    },
    exportTableReject(state) {
      state.exportLoading = false;
    },
    changeTable(state, action: PayloadAction<{ page: number; pageSize: number; sort?: SortObject }>) {
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.sort = action.payload.sort;
      state.loadingData = true;
      localStorage.setItem(pageSizeLsKey, `${action.payload.pageSize}`);
    },
    changeFilters(state, action: PayloadAction<PaymentRequestsFiltersType>) {
      state.filters = action.payload;
      state.page = 1;
      state.loadingData = true;
    },
    openCreateDrawer(state) {
      state.requestDrawerOpen = true;
      state.requestDrawerMode = "create";
      state.selectedRequest = undefined;
    },
    openEditDrawer(state, action: PayloadAction<PaymentRequestModel>) {
      state.requestDrawerOpen = true;
      state.requestDrawerMode = "edit";
      state.detailDrawerOpen = false;
      state.selectedRequest = action.payload;
    },
    closeRequestDrawer(state) {
      if (!state.actionLoading) {
        state.requestDrawerOpen = false;
      }
    },
    createDraft(state, _action: PayloadAction<PaymentRequestFormInput>) {
      state.actionLoading = true;
    },
    createAndSubmit(state, _action: PayloadAction<PaymentRequestFormInput>) {
      state.actionLoading = true;
    },
    updateRequest(state, _action: PayloadAction<{ id: number; form: PaymentRequestFormInput }>) {
      state.actionLoading = true;
    },
    updateAndResubmitRequest(state, _action: PayloadAction<{ id: number; form: PaymentRequestFormInput }>) {
      state.actionLoading = true;
    },
    requestUpdateBeforeResubmitSuccess(state, action: PayloadAction<PaymentRequestModel>) {
      const index = state.requests.findIndex((request) => request.id === action.payload.id);
      if (index >= 0) {
        state.requests[index] = action.payload;
      }
      if (state.selectedRequest?.id === action.payload.id) {
        state.selectedRequest = action.payload;
      }
    },
    requestActionSuccess(state, action: PayloadAction<PaymentRequestModel>) {
      state.actionLoading = false;
      state.requestDrawerOpen = false;
      state.decisionModalOpen = false;
      state.decisionAction = undefined;
      state.loadingData = true;

      const index = state.requests.findIndex((request) => request.id === action.payload.id);
      if (index >= 0) {
        state.requests[index] = action.payload;
      }
      if (state.selectedRequest?.id === action.payload.id) {
        state.selectedRequest = action.payload;
      }
    },
    submitRequest(state, _action: PayloadAction<number>) {
      state.actionLoading = true;
    },
    resubmitRequest(state, _action: PayloadAction<number>) {
      state.actionLoading = true;
    },
    deleteRequest(state, _action: PayloadAction<number>) {
      state.actionLoading = true;
    },
    deleteRequestSuccess(state, action: PayloadAction<number>) {
      state.actionLoading = false;
      state.loadingData = true;
      state.requests = state.requests.filter((request) => request.id !== action.payload);
      if (state.selectedRequest?.id === action.payload) {
        state.selectedRequest = undefined;
        state.detailDrawerOpen = false;
        state.requestDrawerOpen = false;
      }
    },
    openDetailDrawer(state, action: PayloadAction<PaymentRequestModel>) {
      state.detailDrawerOpen = true;
      state.selectedRequest = action.payload;
    },
    openHistoryModal(state, action: PayloadAction<PaymentRequestModel>) {
      state.historyModalOpen = true;
      state.historyLoading = true;
      state.selectedRequest = action.payload;
      state.history = undefined;
    },
    loadHistorySuccess(state, action: PayloadAction<PaymentRequestHistoryModel>) {
      state.historyLoading = false;
      state.history = action.payload;
    },
    loadHistoryReject(state) {
      state.historyLoading = false;
    },
    closeHistoryModal(state) {
      state.historyModalOpen = false;
      state.historyLoading = false;
      state.history = undefined;
    },
    detailRequestLoaded(state, action: PayloadAction<PaymentRequestModel>) {
      const index = state.requests.findIndex((request) => request.id === action.payload.id);
      if (index >= 0) {
        state.requests[index] = action.payload;
      }
      if (state.selectedRequest?.id === action.payload.id) {
        state.selectedRequest = action.payload;
      }
    },
    closeDetailDrawer(state) {
      state.detailDrawerOpen = false;
    },
    openDecisionModal(state, action: PayloadAction<{ request: PaymentRequestModel; action: "approve" | "reject" | "return" }>) {
      state.selectedRequest = action.payload.request;
      state.decisionAction = action.payload.action;
      state.decisionModalOpen = true;
    },
    closeDecisionModal(state) {
      if (!state.actionLoading) {
        state.decisionModalOpen = false;
        state.decisionAction = undefined;
      }
    },
    openBulkDecisionModal(state, action: PayloadAction<{ ids: Array<number>; action: "approve" | "reject" | "return" }>) {
      state.bulkDecisionIds = action.payload.ids;
      state.bulkDecisionAction = action.payload.action;
      state.bulkDecisionModalOpen = true;
    },
    closeBulkDecisionModal(state) {
      if (!state.actionLoading) {
        state.bulkDecisionModalOpen = false;
        state.bulkDecisionAction = undefined;
        state.bulkDecisionIds = [];
      }
    },
    sendDecision(
      state,
      _action: PayloadAction<{ id: number; action: "approve" | "reject" | "return"; form: PaymentRequestDecisionFormInput }>,
    ) {
      state.actionLoading = true;
    },
    sendBulkDecision(
      state,
      _action: PayloadAction<{
        ids: Array<number>;
        action: "approve" | "reject" | "return";
        form: PaymentRequestDecisionFormInput;
      }>,
    ) {
      state.actionLoading = true;
    },
    bulkDecisionSuccess(state, action: PayloadAction<Array<PaymentRequestModel>>) {
      state.actionLoading = false;
      state.bulkDecisionModalOpen = false;
      state.bulkDecisionAction = undefined;
      state.bulkDecisionIds = [];
      state.loadingData = true;

      action.payload.forEach((updatedRequest) => {
        const index = state.requests.findIndex((request) => request.id === updatedRequest.id);
        if (index >= 0) {
          state.requests[index] = updatedRequest;
        }
        if (state.selectedRequest?.id === updatedRequest.id) {
          state.selectedRequest = updatedRequest;
        }
      });
    },
    openManualPaymentModal(state, action: PayloadAction<{ request: PaymentRequestModel; payment?: ManualPaymentModel }>) {
      state.selectedRequest = action.payload.request;
      state.selectedManualPayment = action.payload.payment;
      state.manualPaymentModalOpen = true;
      state.manualPaymentModalMode = action.payload.payment ? "edit" : "create";
      state.paymentsLoading = true;
      state.paymentsLoadingData = true;
      state.paymentsPage = 1;
      state.paymentsFilters = {
        type: ["EXPENSE"],
        currencyId: null,
        status: ["NOT_DISTRIBUTED", "PARTIALLY_DISTRIBUTED"],
      };
      state.selectedPaymentId = action.payload.payment?.paymentId;
      state.selectedPaymentIds = action.payload.payment?.paymentId ? [action.payload.payment.paymentId] : [];
    },
    closeManualPaymentModal(state) {
      if (!state.actionLoading) {
        state.manualPaymentModalOpen = false;
        state.selectedManualPayment = undefined;
        state.payments = undefined;
        state.paymentsTotal = undefined;
        state.paymentsSort = undefined;
        state.paymentsFilters = undefined;
        state.selectedPaymentId = undefined;
        state.selectedPaymentIds = [];
      }
    },
    loadPaymentsSuccess(state, action: PayloadAction<{ payments: Array<PaymentModel>; total: number }>) {
      state.paymentsLoading = false;
      state.paymentsLoadingData = false;
      state.payments = action.payload.payments;
      state.paymentsTotal = action.payload.total;
    },
    changePaymentsTable(state, action: PayloadAction<{ page: number; pageSize: number; sort?: SortObject; filters: PaymentsFiltersType }>) {
      state.paymentsLoadingData = true;
      state.paymentsPage = action.payload.page;
      state.paymentsPageSize = action.payload.pageSize;
      state.paymentsSort = action.payload.sort;
      state.paymentsFilters = action.payload.filters;
      state.selectedPaymentId = undefined;
      state.selectedPaymentIds = [];
    },
    changeSelectedPaymentId(state, action: PayloadAction<number | undefined>) {
      state.selectedPaymentId = action.payload;
      state.selectedPaymentIds = action.payload ? [action.payload] : [];
    },
    changeSelectedPaymentIds(state, action: PayloadAction<Array<number>>) {
      state.selectedPaymentIds = action.payload;
      state.selectedPaymentId = action.payload[0];
    },
    createManualPayment(state, _action: PayloadAction<ManualPaymentFormInput>) {
      state.actionLoading = true;
    },
    createManualPayments(state, _action: PayloadAction<Array<ManualPaymentFormInput>>) {
      state.actionLoading = true;
    },
    updateManualPayment(state, _action: PayloadAction<{ id: number; form: ManualPaymentFormInput }>) {
      state.actionLoading = true;
    },
    deleteManualPayment(state, _action: PayloadAction<number>) {
      state.actionLoading = true;
    },
    actionSuccess(state) {
      state.actionLoading = false;
      state.requestDrawerOpen = false;
      state.decisionModalOpen = false;
      state.bulkDecisionModalOpen = false;
      state.manualPaymentModalOpen = false;
      state.selectedManualPayment = undefined;
      state.decisionAction = undefined;
      state.bulkDecisionAction = undefined;
      state.bulkDecisionIds = [];
      state.loadingData = true;
      state.payments = undefined;
      state.paymentsTotal = undefined;
      state.selectedPaymentId = undefined;
      state.selectedPaymentIds = [];
    },
    manualPaymentActionSuccess(state, action: PayloadAction<PaymentRequestModel | undefined>) {
      state.actionLoading = false;
      state.manualPaymentModalOpen = false;
      state.selectedManualPayment = undefined;
      state.loadingData = true;
      state.payments = undefined;
      state.paymentsTotal = undefined;
      state.selectedPaymentId = undefined;
      state.selectedPaymentIds = [];

      if (action.payload) {
        const index = state.requests.findIndex((request) => request.id === action.payload?.id);
        if (index >= 0) {
          state.requests[index] = action.payload;
        }
        if (state.selectedRequest?.id === action.payload.id) {
          state.selectedRequest = action.payload;
        }
      }
    },
    actionReject(state) {
      state.actionLoading = false;
    },
  },
});

export default paymentRequestsSlice;
